/**
 * @copyright
 * Copyright (c) 2025 Accretive Technology Group, Inc. All rights
 * reserved. For use only by Accretive Technology, its employees
 * and contractors. DO NOT DISTRIBUTE.
 */
import { ActionReducerMapBuilder, AsyncThunk, createSlice, current, PayloadAction } from "@reduxjs/toolkit";

import { RequestStatus } from "~/constants";
import { ReportStatusEnum } from "~/features/reportTracker/constants";
import { ReportDetails } from "~/features/reportTracker/types";
import {
  DEFAULT_FILTER_INTERVAL,
  StatisticsGroupBy,
  StatisticsPlatform,
  StatisticsTab,
} from "~/features/statistics/constants";
import statisticsTabConfigMap from "~/features/statistics/statisticsTabConfigMap";
import {
  autoLoadReportAsync,
  getEmailSubscriptionAsync,
  loadReportAsync,
  loadSitesAsync,
  sendEmailSubscriptionAsync,
} from "~/features/statistics/thunks";
import { FilterOptions, StatisticsState } from "~/features/statistics/types";
import { ReportType } from "~/types";
import getDateInterval from "~/utils/getDateInterval";

const { from, to } = getDateInterval(DEFAULT_FILTER_INTERVAL);

const totalStatsDefaultData = {
  totals: {
    prejns: 0,
    nonprejns: 0,
    newchrgusr: 0,
    prepaidprejns: 0,
    abvthreshusr: 0,
    trstamntsuc: 0,
    chargebackcount: 0,
    chrgbkamnt: 0,
    chrgbkcramnt: 0,
    clicks: 0,
    netpayout: 0,
    uniquelogins: 0,
    netrevenue: 0,
  },
  sites: [],
};

const geoReportDefaultData = {
  totals: {
    prejns: 0,
    nonprejns: 0,
    newchrgusr: 0,
    abvthreshusr: 0,
    trstamntsuc: 0,
    chargebackcount: 0,
    chrgbkamnt: 0,
    chrgbkcramnt: 0,
    netrevenue: 0,
    clicks: 0,
  },
  countries: [],
};

const platformStatsDefaultData = {
  totals: {
    prejns: 0,
    nonprejns: 0,
    newchrgusr: 0,
    abvthreshusr: 0,
    trstamntsuc: 0,
    chargebackcount: 0,
    chrgbkamnt: 0,
    chrgbkcramnt: 0,
    netrevenue: 0,
    clicks: 0,
  },
  platforms: [],
};

const initialState: StatisticsState = {
  isReportLoading: false,
  isFullscreen: false,
  scrollToTable: false,
  openedFromReportTracker: false,
  shouldGetReport: false,
  tab: {
    activeTab: StatisticsTab.TOTAL_STATS,
    columns: {
      [StatisticsTab.TOTAL_STATS]: statisticsTabConfigMap[StatisticsTab.TOTAL_STATS].activeColumns,
      [StatisticsTab.PLATFORM_STATS]: statisticsTabConfigMap[StatisticsTab.PLATFORM_STATS].activeColumns,
      [StatisticsTab.USER_VALUE]: statisticsTabConfigMap[StatisticsTab.USER_VALUE].activeColumns,
      [StatisticsTab.GEO]: statisticsTabConfigMap[StatisticsTab.GEO].activeColumns,
      [StatisticsTab.UPSELL_STATS]: statisticsTabConfigMap[StatisticsTab.UPSELL_STATS].activeColumns,
      [StatisticsTab.PAST_PAYOUTS]: statisticsTabConfigMap[StatisticsTab.PAST_PAYOUTS].activeColumns,
      [StatisticsTab.USER_SPENDING]: statisticsTabConfigMap[StatisticsTab.USER_SPENDING].activeColumns,
      [StatisticsTab.VIDEO_SALES]: statisticsTabConfigMap[StatisticsTab.VIDEO_SALES].activeColumns,
    },
  },
  filter: {
    dev: StatisticsPlatform.ALL,
    selectedSiteIds: [],
    from,
    groupBy: StatisticsGroupBy.DATE,
    to,
    trkid: "",
  },
  emailSubscription: {
    meta: {
      initStatus: RequestStatus.IDLE,
      status: RequestStatus.IDLE,
    },
    data: null,
  },
  referrerSites: {
    meta: {
      status: RequestStatus.IDLE,
    },
    data: {
      sites: {
        byId: {},
        sortedIds: [],
        byName: {},
      },
    },
  },
  openReport: {
    meta: {
      status: RequestStatus.IDLE,
    },
    data: {
      cacheKey: "",
      status: ReportStatusEnum.PENDING,
      params: {
        from: "",
        to: "",
        groupBy: "",
        reportType: "",
        excludedSites: [],
        trackingId: undefined,
        device: undefined,
      },
      data: null,
    },
  },
  localReports: {
    totalStats: {
      meta: {
        status: RequestStatus.IDLE,
      },
      data: {
        totals: {
          prejns: 0,
          nonprejns: 0,
          newchrgusr: 0,
          prepaidprejns: 0,
          abvthreshusr: 0,
          trstamntsuc: 0,
          chargebackcount: 0,
          chrgbkamnt: 0,
          chrgbkcramnt: 0,
          clicks: 0,
          netpayout: 0,
          uniquelogins: 0,
          netrevenue: 0,
        },
        sites: [],
      },
    },
    platformStats: {
      meta: {
        status: RequestStatus.IDLE,
      },
      data: platformStatsDefaultData,
    },
    userValue: {
      meta: {
        status: RequestStatus.IDLE,
      },
      data: {
        totals: {
          prejns: 0,
          usrspndcnt24: 0,
          trstprcntsuc24: 0,
          trstamntsuc24: 0,
          usrspndcnt7: 0,
          trstprcntsuc7: 0,
          trstamntsuc7: 0,
          usrspndcnt30: 0,
          trstprcntsuc30: 0,
          trstamntsuc30: 0,
          usrspndcnt31p: 0,
          ltval: 0,
          usrspndcntlt: 0,
          usrspndprcntlt: 0,
          spendermembervalue24: 0,
          spendermembervalue7: 0,
          spendermembervalue30: 0,
          trstprcntsuc31p: 0,
          trstamntsuc31p: 0,
        },
        sites: [],
      },
    },
    geo: {
      meta: {
        status: RequestStatus.IDLE,
      },
      data: geoReportDefaultData,
    },
    upsellStats: {
      meta: {
        status: RequestStatus.IDLE,
      },
      data: {
        totals: {
          upsell_sales_amount: 0,
          upsell_chargeback_amount: 0,
          upsell_sales_count: 0,
          upsell_chargeback_count: 0,
          upsell_cancel_count: 0,
          upsell_net_sales: 0,
          upsell_reseller_payout: 0,
          upsell_taxamount: 0,
          upsell_chargebackadjustment: 0,
          upsell_creditamount: 0,
          upsell_creditadjustment: 0,
        },
        sites: [],
      },
    },
    pastPayouts: {
      meta: {
        status: RequestStatus.IDLE,
      },
      data: {
        payments: [],
      },
    },
    userSpending: {
      meta: {
        status: RequestStatus.IDLE,
      },
      data: {
        users: [],
      },
    },
    videoSales: {
      meta: {
        status: RequestStatus.IDLE,
      },
      data: {
        totals: {
          videoSalesCount: 0,
          totalVideoSales: "",
          estimatedVideoSalesRevenue: "",
        },
        sites: [],
      },
    },
    dashboard: {
      meta: {
        status: RequestStatus.IDLE,
      },
      data: {
        totalStats: totalStatsDefaultData,
        platformStats: platformStatsDefaultData,
        geoReport: geoReportDefaultData,
      },
    },
  },
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const addReportExtraReducer = <ThunkT extends AsyncThunk<any, any, any>>(
  builder: ActionReducerMapBuilder<StatisticsState>,
  thunk: ThunkT,
  stateProp: ReportType,
) => {
  builder

    .addCase(thunk.pending, (state) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      state.localReports[stateProp].meta.status = RequestStatus.PENDING;
      state.isReportLoading = true;
    })

    .addCase(thunk.fulfilled, (state, action) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      state.localReports[stateProp].meta.status = RequestStatus.SUCCESS;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-member-access
      state.localReports[stateProp].data = action.payload;
      state.isReportLoading = false;
    })

    .addCase(thunk.rejected, (state) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      state.localReports[stateProp].meta.status = RequestStatus.ERROR;
      state.isReportLoading = false;
    });
};

const statisticsSlice = createSlice({
  name: "statistics",
  initialState,
  reducers: {
    setScrollToTable: (state, action: PayloadAction<boolean>) => {
      state.scrollToTable = action.payload;
    },
    setIsFullscreen: (state, action: PayloadAction<boolean>) => {
      state.isFullscreen = action.payload;
    },

    setActiveTab: (state, action: PayloadAction<StatisticsTab>) => {
      state.tab.activeTab = action.payload;
      state.filter.groupBy = StatisticsGroupBy.DATE;
    },

    setIsReportLoading: (state, action: PayloadAction<boolean>) => {
      state.isReportLoading = action.payload;
    },

    setFields: (state, action: PayloadAction<string[]>) => {
      const activeTab = state.tab.activeTab;
      const fields = current(state.tab.columns[activeTab]);

      state.tab.columns[activeTab] = Object.fromEntries(
        Object.keys(fields).map((key) => [key, action.payload.includes(key)]),
      );
    },

    toggleField: (state, action: PayloadAction<string>) => {
      const activeTab = state.tab.activeTab;
      const field = state.tab.columns[activeTab][action.payload];

      if (field !== undefined) {
        state.tab.columns[activeTab][action.payload] = !field;
      }
    },

    setAllField: (state, action: PayloadAction<boolean>) => {
      const activeTab = state.tab.activeTab;
      const fields = current(state.tab.columns[activeTab]);

      state.tab.columns[activeTab] = Object.fromEntries(Object.keys(fields).map((key) => [key, action.payload]));
    },

    setFilter: (state, action: PayloadAction<Partial<FilterOptions>>) => {
      state.filter = {
        ...state.filter,
        ...action.payload,
      };
    },

    setOpenedFromReportTracker: (state, action: PayloadAction<boolean>) => {
      state.openedFromReportTracker = action.payload;
    },

    setShouldGetReport: (state, action: PayloadAction<boolean>) => {
      state.shouldGetReport = action.payload;
    },

    setOpenReportStatus: (state, action: PayloadAction<RequestStatus>) => {
      state.openReport.meta.status = action.payload;
    },

    setOpenReportData: (state, action: PayloadAction<ReportDetails>) => {
      state.openReport.data = action.payload;
    },

    setReportData: (
      state,
      action: PayloadAction<{
        type: ReportType;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        payload: any;
      }>,
    ) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-member-access
      state.localReports[action.payload.type].data = action.payload.payload;
    },

    setReportCacheKey: (
      state,
      action: PayloadAction<{
        type: ReportType;
        payload?: string;
      }>,
    ) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      state.localReports[action.payload.type].cacheKey = action.payload.payload;
    },

    setReportStatus: (
      state,
      action: PayloadAction<{
        type: ReportType;
        payload: RequestStatus;
      }>,
    ) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-member-access
      state.localReports[action.payload.type].meta.status = action.payload.payload;
      state.isReportLoading = action.payload.payload === RequestStatus.PENDING;
    },

    setReport: (
      state,
      action: PayloadAction<{
        type: ReportType;
        status: RequestStatus;
        cacheKey?: string;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        data: any;
      }>,
    ) => {
      // Set status
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      state.localReports[action.payload.type].meta.status = action.payload.status;
      state.isReportLoading = action.payload.status === RequestStatus.PENDING;

      // Set cache key
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      state.localReports[action.payload.type].cacheKey = action.payload.cacheKey;

      // Set data
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      state.localReports[action.payload.type].data = action.payload.data;
    },

    clearState: () => initialState,
  },
  extraReducers: (builder) => {
    builder

      // Referrer sites
      .addCase(loadSitesAsync.pending, (state) => {
        state.referrerSites.meta.status = RequestStatus.PENDING;
      })

      .addCase(loadSitesAsync.fulfilled, (state, action) => {
        state.referrerSites.meta.status = RequestStatus.SUCCESS;
        state.referrerSites.data.sites = action.payload;

        //this means no data in local storage
        if (state.filter.selectedSiteIds.length < 1) {
          state.filter.selectedSiteIds = action.payload.sortedIds;
        }
      })

      .addCase(loadSitesAsync.rejected, (state) => {
        state.referrerSites.meta.status = RequestStatus.ERROR;
      })

      // Email subscription
      .addCase(getEmailSubscriptionAsync.pending, (state) => {
        state.emailSubscription.meta.initStatus = RequestStatus.PENDING;
      })

      .addCase(getEmailSubscriptionAsync.fulfilled, (state, action) => {
        state.emailSubscription.meta.initStatus = RequestStatus.SUCCESS;
        state.emailSubscription.data = action.payload;
      })

      .addCase(getEmailSubscriptionAsync.rejected, (state) => {
        state.emailSubscription.meta.initStatus = RequestStatus.ERROR;
      })

      .addCase(sendEmailSubscriptionAsync.pending, (state) => {
        state.emailSubscription.meta.status = RequestStatus.PENDING;
      })

      .addCase(sendEmailSubscriptionAsync.fulfilled, (state) => {
        state.emailSubscription.meta.status = RequestStatus.SUCCESS;
      })

      .addCase(sendEmailSubscriptionAsync.rejected, (state) => {
        state.emailSubscription.meta.status = RequestStatus.ERROR;
      })

      .addCase(loadReportAsync.fulfilled, (state, action) => {
        state.openReport.data = action.payload;
      });

    addReportExtraReducer(builder, autoLoadReportAsync, "totalStats");
  },
});

export const StatisticsActions = statisticsSlice.actions;

export const statisticsReducer = statisticsSlice.reducer;
